import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Search, Download, RefreshCw, Mail, Edit3, Tag, ExternalLink, ChevronLeft,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const STATUS_COLORS = {
  unverified: 'bg-amber-100 text-amber-700 border-amber-200',
  used: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  expired: 'bg-slate-200 text-slate-700 border-slate-300',
  invalid: 'bg-red-100 text-red-700 border-red-200',
  error: 'bg-red-100 text-red-700 border-red-200',
};

const GrouponSignups = () => {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [byStatus, setByStatus] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [editing, setEditing] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [actionLoading, setActionLoading] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (status !== 'all') params.set('status', status);
      const res = await api.get(`/admin/groupon-signups?${params.toString()}`);
      setItems(res.data.items || []);
      setByStatus(res.data.by_status || {});
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load sign-ups');
    } finally {
      setLoading(false);
    }
  }, [api, q, status]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openEdit = (row) => {
    setEditing(row);
    setEditStatus(row.status);
    setEditNotes(row.admin_notes || '');
  };

  const saveEdit = async () => {
    if (!editing) return;
    setActionLoading((s) => ({ ...s, [`save-${editing.id}`]: true }));
    try {
      await api.patch(`/admin/groupon-signups/${editing.id}`, {
        status: editStatus,
        admin_notes: editNotes,
      });
      toast.success('Sign-up updated');
      setEditing(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setActionLoading((s) => ({ ...s, [`save-${editing.id}`]: false }));
    }
  };

  const resendEmail = async (row) => {
    setActionLoading((s) => ({ ...s, [`email-${row.id}`]: true }));
    try {
      const res = await api.post(`/admin/groupon-signups/${row.id}/resend-email`);
      if (res.data.success) toast.success('Login email re-sent');
      else toast.error(res.data.error || 'Email failed');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Email failed');
    } finally {
      setActionLoading((s) => ({ ...s, [`email-${row.id}`]: false }));
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get('/admin/groupon-signups/export.csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `groupon-signups-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('en-GB'); } catch (e) { return iso; }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="groupon-signups-page">
      <div className="max-w-7xl mx-auto">
        <Link to="/admin" className="inline-flex items-center gap-1 text-slate-600 hover:text-emerald-600 text-sm font-medium mb-4">
          <ChevronLeft className="w-4 h-4" /> Back to Admin Dashboard
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Tag className="w-7 h-7 text-pink-500" /> Groupon Sign-Ups
            </h1>
            <p className="text-slate-500 text-sm mt-1">Direct app sign-ups using a Groupon code — separate from the assessment route.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData} className="gap-2" data-testid="refresh-signups"><RefreshCw className="w-4 h-4" />Refresh</Button>
            <Button variant="outline" onClick={exportCsv} className="gap-2" data-testid="export-signups-csv"><Download className="w-4 h-4" />Export CSV</Button>
          </div>
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            { l: 'Total', n: total, color: 'bg-white' },
            { l: 'Unverified', n: byStatus.unverified || 0, color: 'bg-amber-50' },
            { l: 'Used', n: byStatus.used || 0, color: 'bg-emerald-50' },
            { l: 'Invalid', n: byStatus.invalid || 0, color: 'bg-red-50' },
            { l: 'Expired', n: byStatus.expired || 0, color: 'bg-slate-100' },
          ].map((s) => (
            <div key={s.l} className={`${s.color} border border-slate-200 rounded-xl p-4`}>
              <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">{s.l}</p>
              <p className="text-2xl font-bold text-slate-800">{s.n}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[220px] relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search code, parent name, email, student..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="signups-search"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44" data-testid="signups-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="unverified">Unverified</SelectItem>
              <SelectItem value="used">Used</SelectItem>
              <SelectItem value="invalid">Invalid</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-500">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-500">
              No Groupon sign-ups yet. Direct sign-ups will appear here once parents register through the app.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {['Date', 'Code', 'Parent', 'Phone', 'Student', 'Year', 'Subjects', 'Email', 'Status', 'Actions'].map((h) => (
                      <th key={h} className="text-left px-3 py-2.5 font-semibold text-slate-600">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((row) => (
                    <tr key={row.id} className="hover:bg-slate-50" data-testid={`signup-row-${row.id}`}>
                      <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{fmt(row.created_at)}</td>
                      <td className="px-3 py-2.5 font-mono font-bold">{row.code}</td>
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-slate-800">{row.parent_name}</div>
                        <div className="text-xs text-slate-500">{row.parent_email}</div>
                      </td>
                      <td className="px-3 py-2.5">{row.parent_phone || '—'}</td>
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-slate-800">{row.student_name}</div>
                        <div className="text-xs text-slate-500 font-mono">{row.student_username || '—'}</div>
                      </td>
                      <td className="px-3 py-2.5">{row.student_year_group}</td>
                      <td className="px-3 py-2.5 text-xs">
                        {(row.subjects_enrolled || []).map((s) => (
                          <span key={s} className="inline-block bg-slate-100 px-1.5 py-0.5 rounded mr-1 capitalize">{s}</span>
                        ))}
                      </td>
                      <td className="px-3 py-2.5">
                        {row.email_sent
                          ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Sent</Badge>
                          : <Badge className="bg-slate-100 text-slate-600 border-slate-200">Not sent</Badge>}
                      </td>
                      <td className="px-3 py-2.5">
                        <Badge className={`${STATUS_COLORS[row.status] || 'bg-slate-100'} capitalize border`}>{row.status}</Badge>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(row)} title="Edit status / notes" data-testid={`edit-${row.id}`}>
                            <Edit3 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => resendEmail(row)} title="Resend login email" disabled={!!actionLoading[`email-${row.id}`]} data-testid={`resend-${row.id}`}>
                            <Mail className="w-4 h-4" />
                          </Button>
                          {row.parent_id && (
                            <Link to={`/admin/users?q=${encodeURIComponent(row.parent_email)}`} title="View user">
                              <Button size="sm" variant="ghost"><ExternalLink className="w-4 h-4" /></Button>
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Update Groupon sign-up</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
                <p className="font-mono font-bold">{editing.code}</p>
                <p className="text-slate-600">{editing.parent_name} — {editing.parent_email}</p>
                <p className="text-slate-600">Student: {editing.student_name} (Year {editing.student_year_group})</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold">Status</label>
                <Select value={editStatus} onValueChange={setEditStatus}>
                  <SelectTrigger data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unverified">Unverified</SelectItem>
                    <SelectItem value="used">Used / Verified</SelectItem>
                    <SelectItem value="invalid">Invalid</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold">Admin notes</label>
                <Textarea rows={4} value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Internal notes about this sign-up…" data-testid="edit-admin-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-emerald-600 hover:bg-emerald-700" disabled={!!actionLoading[`save-${editing?.id}`]} data-testid="save-edit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GrouponSignups;
